// Permissions par rôle — source de vérité unique côté front.
// Les chemins correspondent aux `to` de la navigation (App.js / Sidebar.js).

export const ROLE_LABELS = {
  admin: 'Administrateur',
  direction: 'Direction',
  equipe: 'Équipe',
  comptable: 'Comptable',
  salarie: 'Salarié (espace perso)',
  structure: 'Crèche (tablette)',
};

const ALL_MODULES = [
  '/', '/enfants', '/suivi-medical', '/devis', '/contrats', '/demandes', '/preinscriptions',
  '/transmissions', '/rh', '/planning', '/planning-enfants', '/echeances', '/finance', '/analytics', '/statistiques', '/documents',
  '/protocoles', '/ressources-docs', '/signatures', '/depot', '/tarifs', '/alertes', '/utilisateurs', '/marque', '/courriers', '/whatsapp',
  '/parametres', '/mon-espace',
];

export const PERMISSIONS = {
  admin: {
    modules: ALL_MODULES,
    canSeeFinancials: true,
    home: '/',
  },
  direction: {
    // Tout sauf le financier (Finance, Analyse, Grille tarifaire) et la gestion des comptes
    modules: [
      '/', '/enfants', '/suivi-medical', '/devis', '/contrats', '/demandes', '/preinscriptions',
      '/transmissions', '/rh', '/planning', '/planning-enfants', '/echeances', '/documents', '/protocoles', '/ressources-docs', '/signatures', '/depot', '/alertes', '/courriers', '/whatsapp',
      '/mon-espace',
    ],
    canSeeFinancials: false, // masque aussi marge/CA dans Dashboard, Demandes, Devis
    home: '/',
  },
  equipe: {
    // Enfants, Transmissions, Planning (onglet RH) et Protocoles (lecture)
    modules: ['/enfants', '/suivi-medical', '/transmissions', '/rh', '/planning', '/planning-enfants', '/protocoles', '/ressources-docs', '/mon-espace'],
    canSeeFinancials: false,
    rhTabs: ['planning'], // restreint la page RH à l'onglet Planning
    home: '/enfants',
  },
  comptable: {
    // Accès SUR AUTORISATION, limité à la facturation (lecture + export FEC).
    modules: ['/finance'],
    canSeeFinancials: true,
    readOnlyFinance: true, // masque les actions d'écriture (valider, marquer payé, générer)
    home: '/finance',
  },
  salarie: {
    // Compte nominatif d'un salarié : son espace RH personnel (congés, heures,
    // planning de la semaine), le planning de l'équipe de sa crèche (consultation)
    // et les protocoles à lire/signer. + le compte de sa crèche si l'admin l'ouvre.
    modules: ['/mon-espace', '/rh', '/protocoles'],
    canSeeFinancials: false,
    rhTabs: ['planning'], // la page RH se limite au planning équipe
    lockStructure: true,  // aucune structure à choisir : son espace est personnel
    hideOutils: true,     // ni alertes réglementaires, ni assistant Georges
    home: '/mon-espace',
  },
  structure: {
    // Compte partagé d'une crèche (tablette du matin) : uniquement le quotidien
    // des enfants de SA structure. Ni RH, ni facturation, ni comptes.
    modules: ['/transmissions', '/enfants', '/suivi-medical', '/planning-enfants', '/protocoles'],
    canSeeFinancials: false,
    lockStructure: true, // la structure est imposée : pas de sélecteur dans le menu
    hideOutils: true,    // ni alertes réglementaires, ni assistant Georges sur la tablette
    home: '/transmissions',
  },
};

// Accès « compte crèche » : la tablette, ou un salarié à qui l'admin a ouvert
// le compte de sa crèche (case « Accès au compte crèche » dans Comptes & accès).
export function estCompteCreche(user) {
  return !!user && (user.role === 'structure' || (user.role === 'salarie' && !!user.acces_creche));
}

export function can(role, path, user = null) {
  const p = PERMISSIONS[role];
  if (!p) return false;
  if (p.modules.includes(path)) return true;
  return estCompteCreche(user) && role !== 'structure' && PERMISSIONS.structure.modules.includes(path);
}

export function canSeeFinancials(role) {
  return !!PERMISSIONS[role]?.canSeeFinancials;
}

export function isReadOnlyFinance(role) {
  return !!PERMISSIONS[role]?.readOnlyFinance;
}

export function homeFor(role) {
  return PERMISSIONS[role]?.home || '/enfants';
}

// Le compte est-il cantonné à sa propre structure (sélecteur figé) ?
export function isStructureLocked(role) {
  return !!PERMISSIONS[role]?.lockStructure;
}

// Les outils transverses (alertes réglementaires, assistant Georges) sont réservés
// aux comptes de gestion — ils donnent une vue d'ensemble hors périmètre.
export function showsOutils(role) {
  return !PERMISSIONS[role]?.hideOutils;
}

export function allowedRhTabs(role) {
  return PERMISSIONS[role]?.rhTabs || null; // null = tous les onglets
}
